import type { SelectPopoverProps } from './SelectPopover';

import { ActionList, Icon, Scrollable, Text } from '@shopify/polaris';
import { useControlDataChange } from '~/presentation/reports/common';
import { CheckIcon } from '@shopify/polaris-icons';
import { PopoverStatus } from './PopoverStatus';
import { useMemo } from 'react';

type PopoverActionListProps = Pick<
  SelectPopoverProps,
  | 'options'
  | 'defaultValue'
  | 'id'
  | 'isLoading'
  | 'onChangeValue'
  | 'togglePopoverActive'
  | 'enableCreateOption'
  | 'maxHeight'
  | 'minHeight'
>;

export const PopoverActionList: React.FC<PopoverActionListProps> = ({
  options,
  defaultValue,
  id,
  isLoading,
  enableCreateOption,
  maxHeight,
  minHeight,
  onChangeValue,
  togglePopoverActive,
}) => {
  const { emitControlChange } = useControlDataChange();
  const isNoData = options?.length === 0;

  const formattedOptions = useMemo(() => {
    if (!options?.length) return [];
    let exitsValue = false;

    const result = options.map((option) => {
      if (option.value === defaultValue) {
        exitsValue = true;
      }
      return {
        ...option,
      };
    });
    if (!exitsValue && typeof defaultValue === 'string' && enableCreateOption) {
      result.push({
        label: defaultValue,
        value: defaultValue,
      });
    }

    return result;
  }, [options, defaultValue, enableCreateOption]);

  return (
    <>
      {!isNoData && (
        <Scrollable scrollbarWidth="thin" style={{ maxHeight: maxHeight ?? '400px', minHeight }}>
          <ActionList
            actionRole="menuitem"
            items={formattedOptions?.map(({ value, label, description }) => ({
              id: value,
              content: label,
              onAction: () => {
                if (!onChangeValue) {
                  emitControlChange({ [id]: value });
                }
                onChangeValue?.(id, value);
                togglePopoverActive(false);
              },
              truncate: true,
              active: value === defaultValue,
              suffix: value === defaultValue && <Icon source={CheckIcon} tone="success" />,
              helpText: (
                <Text variant="bodySm" as="span" truncate tone="disabled">
                  {description}
                </Text>
              ),
            }))}
          />
        </Scrollable>
      )}
      <PopoverStatus isLoading={isLoading} isShowNoData={isNoData} />
    </>
  );
};
